import React, { useState, useEffect, useRef } from "react";
import Select from "react-select";
import {
    Card,
    CardBody,
    Col,
    Container,
    Form,
    Input,
    Label,
    Row,
    Button,
    Table,
    Nav,
    NavItem,
    NavLink,
    TabContent,
    TabPane,
    FormGroup,
} from "reactstrap";
import classnames from "classnames";
import { SquarePen, Eye, Trash2, PlusCircle, List } from "lucide-react";
import Swal from "sweetalert2";
import Breadcrumb from "../../components/Common/Breadcrumb";
import {
    hasPagePermissionWithAdmin,
    hasViewOnlyPermission,
    hasEditOrHigherPermission,
    hasDeletePermissionWithAdmin,
} from "../../components/Permission/PermissionService";
import { getTemplates, saveTemplate, deleteTemplate } from "../../components/Api/GmsMessageTemplate";
import { getBusinesses } from "../../components/Api/GmsBusiness";
import { getBranches } from "../../components/Api/GmsBranch";

const labelStyle = { color: "#556ee6", fontWeight: "500" };

const selectStyles = {
    control: (provided, state) => ({
        ...provided,
        minHeight: '38px',
        borderColor: state.isFocused ? '#556ee6' : '#ced4da',
        boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
    }),
};

const RECIPIENT_OPTIONS = [
    { value: 'cus', label: 'Customer' },
    { value: 'emp', label: 'Employee' },
];

const ACTION_OPTIONS = [
    { value: 'bok', label: 'Booking' },
    { value: 'che', label: 'Check-in' },
    { value: 'bil', label: 'Billing' },
];

const TOKENS = [
    { label: 'Customer Name', value: '{{CustomerName}}' },
    { label: 'Employee Name', value: '{{EmployeeName}}' },
    { label: 'Date', value: '{{Date}}' },
    { label: 'Time', value: '{{Time}}' },
    { label: 'Business Name', value: '{{BusinessName}}' },
    { label: 'Branch Name', value: '{{BranchName}}' },
];

const DEFAULT_TEMPLATES = {
    'bok': {
        'cus': "Hello {{CustomerName}}, your booking at {{BranchName}} is confirmed for {{Date}} {{Time}}. We look forward to seeing you!",
        'emp': "New booking notification: {{CustomerName}} has a booking at {{BranchName}} on {{Date}} at {{Time}}. Assigned to: {{EmployeeName}}."
    },
    'che': {
        'cus': "Welcome to {{BusinessName}}, {{CustomerName}}! You have successfully checked in at {{BranchName}}.",
        'emp': "Client {{CustomerName}} has arrived and checked in at {{BranchName}}."
    },
    'bil': {
        'cus': "Thank you for visiting {{BusinessName}}! Your billing is complete at {{BranchName}}. Date: {{Date}}. Amount: {{Amount}}",
        'emp': "Transaction completed: {{CustomerName}} has been billed at {{BranchName}}."
    }
};

const defaultForm = {
    template_id: null,
    template_name: "",
    content: "",
    recepient: "",
    action: "",
    business_id: null,
    branchid: null,
    status: true,
};

export default function MessageTemplatePage() {
    const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
    const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsMessageTemplate');
    const isViewOnly = hasViewOnlyPermission(permissions, '/GmsMessageTemplate');
    const hasEditOrHigher = hasEditOrHigherPermission(permissions, '/GmsMessageTemplate');
    const hasDeletePermission = hasDeletePermissionWithAdmin(permissions, '/GmsMessageTemplate');

    const [activeTab, setActiveTab] = useState("1");
    const [templates, setTemplates] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [branches, setBranches] = useState([]);
    const [form, setForm] = useState(defaultForm);
    const [loading, setLoading] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const contentRef = useRef(null);

    const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
    const userId = authUser.userId || 1;

    useEffect(() => {
        if (hasPermission) {
            loadInitialData();
        }
    }, [hasPermission]);

    const loadInitialData = async () => {
        try {
            setLoading(true);
            const [templateData, bizData, branchData] = await Promise.all([
                getTemplates(),
                getBusinesses({}),
                getBranches({})
            ]);
            const templateArray = templateData?.data || (Array.isArray(templateData) ? templateData : []);
            const bizArray = bizData?.data || (Array.isArray(bizData) ? bizData : []);
            const branchArray = branchData?.data || (Array.isArray(branchData) ? branchData : []);

            setTemplates(templateArray);
            setCompanies(bizArray);
            setBranches(branchArray);
        } catch (error) {
            console.error("Error loading data:", error);
            Swal.fire("Error", "Failed to load page data", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSelectChange = (name, selected) => {
        const value = selected ? selected.value : null;

        setForm(prev => {
            const nextForm = { ...prev, [name]: value };

            // Auto-populate content if action and recipient are selected
            if ((name === 'action' || name === 'recepient') && nextForm.action && nextForm.recepient && !isEditing) {
                const currentDefault = DEFAULT_TEMPLATES[prev.action]?.[prev.recepient] || "";

                // Only overwrite if content is empty OR if the content currently matches the default for the PREVIOUS selection
                if (!prev.content || prev.content === currentDefault) {
                    nextForm.content = DEFAULT_TEMPLATES[nextForm.action]?.[nextForm.recepient] || "";
                }
            }

            return nextForm;
        });
    };

    const insertToken = (token) => {
        if (!contentRef.current) return;

        const textarea = contentRef.current;
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const text = form.content;
        const before = text.substring(0, start);
        const after = text.substring(end);

        const newContent = before + token + after;
        setForm(prev => ({ ...prev, content: newContent }));

        // Set focus back and move cursor after token
        setTimeout(() => {
            textarea.focus();
            textarea.setSelectionRange(start + token.length, start + token.length);
        }, 0);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!form.business_id || !form.branchid || !form.action || !form.recepient || !form.template_name || !form.content) {
            Swal.fire("Error", "Please fill all required fields", "warning");
            return;
        }

        // Check for duplicate active template
        if (form.status) {
            const duplicate = templates.find(t =>
                t.business_id === form.business_id &&
                t.branchid === form.branchid &&
                t.action === form.action &&
                t.recepient === form.recepient &&
                t.status === true &&
                t.template_id !== form.template_id
            );

            if (duplicate) {
                Swal.fire({
                    title: "Duplicate Active Template",
                    text: "An active template already exists for this Branch, Action, and Recipient. Please inactive the previous template before setting this one to active.",
                    icon: "warning"
                });
                return;
            }
        }

        try {
            setLoading(true);
            const submitData = {
                ...form,
                created_by: isEditing ? form.created_by : userId,
                created_on: isEditing ? form.created_on : new Date().toISOString(),
                modified_by: isEditing ? userId : null,
                modified_on: isEditing ? new Date().toISOString() : null,
            };

            await saveTemplate(submitData);
            Swal.fire("Success", `Template ${isEditing ? 'updated' : 'saved'} successfully`, "success");
            resetForm();
            loadInitialData();
            // Removed redirection to tab 2 as per request
        } catch (error) {
            console.error("Error saving template:", error);
            Swal.fire("Error", "Failed to save template", "error");
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setForm(defaultForm);
        setIsEditing(false);
    };

    const handleEdit = (template) => {
        setForm(template);
        setIsEditing(true);
        setActiveTab("1");
        window.scrollTo(0, 0);
    };

    const handleView = (template) => {
        Swal.fire({
            title: `<strong>${template.template_name}</strong>`,
            html: `
                <div style="text-align: left; background: #f8f9fa; padding: 15px; border-radius: 8px; border: 1px solid #e9ecef;">
                    <p style="margin-bottom: 5px; color: #1c7ed6; font-weight: 600;">Message Content:</p>
                    <div style="white-space: pre-wrap; font-family: inherit;">${template.content}</div>
                </div>
            `,
            confirmButtonText: 'Close',
            confirmButtonColor: '#1c7ed6'
        });
    };

    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: "Are you sure?",
            text: "You won't be able to revert this!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, delete it!"
        });

        if (result.isConfirmed) {
            try {
                await deleteTemplate(id);
                Swal.fire("Deleted!", "Template has been deleted.", "success");
                loadInitialData();
            } catch (error) {
                Swal.fire("Error", "Failed to delete template", "error");
            }
        }
    };

    if (!hasPermission) {
        return (
            <div className="page-content">
                <Container fluid>
                    <div className="alert alert-danger">You do not have permission to access this page.</div>
                </Container>
            </div>
        );
    }

    return (
        <div className="page-content">
            <Container fluid>
                <Breadcrumb title="Message Settings" breadcrumbItem="Message Templates" />

                <Row>
                    <Col lg="12">
                        <Card>
                            <CardBody>
                                <Nav tabs className="nav-tabs-custom">
                                    {!isViewOnly && (
                                        <NavItem>
                                            <NavLink
                                                style={{
                                                    cursor: "pointer",
                                                    fontWeight: activeTab === "1" ? "bold" : "normal",
                                                    color: activeTab === "1" ? "#556ee6" : "#74788d",
                                                    borderBottom: activeTab === "1" ? "2px solid #556ee6" : "none",
                                                    padding: "15px 25px",
                                                    fontSize: "16px",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "8px"
                                                }}
                                                className={classnames({ active: activeTab === "1" })}
                                                onClick={() => { if (hasEditOrHigher) setActiveTab("1"); }}
                                            >
                                                <i className="bx bx-plus-circle"></i>
                                                <span>{isEditing ? "Edit Template" : "Message Template Form"}</span>
                                            </NavLink>
                                        </NavItem>
                                    )}
                                    <NavItem>
                                        <NavLink
                                            style={{
                                                cursor: "pointer",
                                                fontWeight: activeTab === "2" ? "bold" : "normal",
                                                color: activeTab === "2" ? "#556ee6" : "#74788d",
                                                borderBottom: activeTab === "2" ? "2px solid #556ee6" : "none",
                                                padding: "15px 25px",
                                                fontSize: "16px",
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "8px"
                                            }}
                                            className={classnames({ active: activeTab === "2" })}
                                            onClick={() => setActiveTab("2")}
                                        >
                                            <i className="bx bx-list-ul"></i>
                                            <span>Template List</span>
                                        </NavLink>
                                    </NavItem>
                                </Nav>

                                <TabContent activeTab={activeTab} className="p-3 text-muted">
                                    <TabPane tabId="1">
                                        <Form onSubmit={handleSubmit}>
                                            <Row>
                                                <Col md="6">
                                                    <FormGroup className="mb-4">
                                                        <Label style={labelStyle} className="mb-2">Company <span className="text-danger">*</span></Label>
                                                        <Select
                                                            options={companies.filter(c => c.business_Active !== false).map(c => ({ value: c.business_Id, label: c.business_NameEn }))}
                                                            styles={selectStyles}
                                                            value={companies.map(c => ({ value: c.business_Id, label: c.business_NameEn })).find(opt => opt.value === form.business_id)}
                                                            onChange={(val) => handleSelectChange('business_id', val)}
                                                            isDisabled={isViewOnly}
                                                            placeholder="Select Company"
                                                        />
                                                    </FormGroup>
                                                </Col>
                                                <Col md="6">
                                                    <FormGroup className="mb-4">
                                                        <Label style={labelStyle} className="mb-2">Branch <span className="text-danger">*</span></Label>
                                                        <Select
                                                            options={branches
                                                                .filter(b => (b.business_Id === form.business_id || b.branch_Buisness_Id === form.business_id) && b.branch_Active !== false)
                                                                .map(b => ({ value: b.branch_Id, label: b.branch_NameEn }))}
                                                            styles={selectStyles}
                                                            value={branches.map(b => ({ value: b.branch_Id, label: b.branch_NameEn })).find(opt => opt.value === form.branchid)}
                                                            onChange={(val) => handleSelectChange('branchid', val)}
                                                            isDisabled={!form.business_id || isViewOnly}
                                                            placeholder="Select Branch"
                                                        />
                                                    </FormGroup>
                                                </Col>
                                                <Col md="6">
                                                    <FormGroup className="mb-4">
                                                        <Label style={labelStyle} className="mb-2">Action <span className="text-danger">*</span></Label>
                                                        <Select
                                                            options={ACTION_OPTIONS}
                                                            styles={selectStyles}
                                                            value={ACTION_OPTIONS.find(opt => opt.value === form.action)}
                                                            onChange={(val) => handleSelectChange('action', val)}
                                                            isDisabled={isViewOnly}
                                                        />
                                                    </FormGroup>
                                                </Col>
                                                <Col md="6">
                                                    <FormGroup className="mb-4">
                                                        <Label style={labelStyle} className="mb-2">Recipient <span className="text-danger">*</span></Label>
                                                        <Select
                                                            options={RECIPIENT_OPTIONS}
                                                            styles={selectStyles}
                                                            value={RECIPIENT_OPTIONS.find(opt => opt.value === form.recepient)}
                                                            onChange={(val) => handleSelectChange('recepient', val)}
                                                            isDisabled={isViewOnly}
                                                        />
                                                    </FormGroup>
                                                </Col>
                                                <Col md="12">
                                                    <FormGroup className="mb-4">
                                                        <Label style={labelStyle} className="mb-2">Template Name <span className="text-danger">*</span></Label>
                                                        <Input
                                                            name="template_name"
                                                            type="text"
                                                            value={form.template_name}
                                                            onChange={handleInputChange}
                                                            readOnly={isViewOnly}
                                                            placeholder="Enter template name (e.g. Booking Confirmation - Client)"
                                                        />
                                                    </FormGroup>
                                                </Col>
                                                <Col md="9">
                                                    <FormGroup className="mb-4">
                                                        <Label style={labelStyle} className="mb-2">Content <span className="text-danger">*</span></Label>
                                                        <Input
                                                            innerRef={contentRef}
                                                            name="content"
                                                            type="textarea"
                                                            rows="5"
                                                            value={form.content}
                                                            onChange={handleInputChange}
                                                            readOnly={isViewOnly}
                                                            placeholder="Type your message here..."
                                                        />
                                                    </FormGroup>
                                                </Col>
                                                <Col md="3">
                                                    <Label style={labelStyle} className="mb-2">Injected Tokens</Label>
                                                    <div className="d-flex flex-wrap gap-2 mb-4">
                                                        {TOKENS.map(token => (
                                                            <Button
                                                                key={token.value}
                                                                color="info"
                                                                outline
                                                                size="sm"
                                                                onClick={() => insertToken(token.value)}
                                                                disabled={isViewOnly}
                                                                title={`Insert ${token.label}`}
                                                                style={{ borderRadius: "5px" }}
                                                            >
                                                                {token.label}
                                                            </Button>
                                                        ))}
                                                    </div>
                                                    <div className="mt-5 pt-3 border-top">
                                                        <p className="text-info small mb-0" style={{ lineHeight: '1.8' }}>
                                                            <i className="bx bx-info-circle mr-1"></i>
                                                            Click on a button to insert the token at cursor position.
                                                        </p>
                                                    </div>
                                                </Col>
                                                <Col md="6">
                                                    <FormGroup check className="mt-2">
                                                        <Label check style={labelStyle} className="mb-2">
                                                            <Input
                                                                type="checkbox"
                                                                name="status"
                                                                checked={form.status}
                                                                onChange={handleInputChange}
                                                                disabled={isViewOnly || !hasDeletePermission}
                                                            />{" "}
                                                            Active
                                                        </Label>
                                                    </FormGroup>
                                                </Col>
                                            </Row>
                                            <div className="d-flex gap-3 mt-4">
                                                {!isViewOnly && (
                                                    <Button color="primary" type="submit" disabled={loading}>
                                                        {loading ? "Processing..." : (isEditing ? "Update Template" : "Save Template")}
                                                    </Button>
                                                )}
                                                <Button color="secondary" type="button" onClick={resetForm} disabled={loading}>
                                                    Clear
                                                </Button>
                                            </div>
                                        </Form>
                                    </TabPane>

                                    <TabPane tabId="2">
                                        <div className="table-responsive">
                                            <Table className="table mb-0">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th style={{ color: "black", fontWeight: "600" }}>Template Name</th>
                                                        <th style={{ color: "black", fontWeight: "600" }}>Action</th>
                                                        <th style={{ color: "black", fontWeight: "600" }}>Recipient</th>
                                                        <th style={{ color: "black", fontWeight: "600" }}>Branch</th>
                                                        <th style={{ color: "black", fontWeight: "600" }}>Status</th>
                                                        <th style={{ color: "black", fontWeight: "600" }}>Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {templates.length > 0 ? (
                                                        templates.map((t) => (
                                                            <tr key={t.template_id}>
                                                                <td>{t.template_name}</td>
                                                                <td>{ACTION_OPTIONS.find(a => a.value === t.action)?.label}</td>
                                                                <td>{RECIPIENT_OPTIONS.find(r => r.value === t.recepient)?.label}</td>
                                                                <td>{branches.find(b => b.branch_Id === t.branchid)?.branch_NameEn}</td>
                                                                <td>
                                                                    <span className={`badge badge-pill badge-soft-${t.status ? 'success' : 'danger'}`}>
                                                                        {t.status ? 'Active' : 'Inactive'}
                                                                    </span>
                                                                </td>
                                                                <td>
                                                                    <div className="d-flex gap-3">
                                                                        <Button
                                                                            color="soft-info"
                                                                            size="sm"
                                                                            onClick={() => handleView(t)}
                                                                            title="View Content"
                                                                            style={{ borderRadius: "6px" }}
                                                                        >
                                                                            <Eye size={16} />
                                                                        </Button>
                                                                        <Button
                                                                            color="soft-primary"
                                                                            size="sm"
                                                                            onClick={() => handleEdit(t)}
                                                                            title="Edit Template"
                                                                            style={{ borderRadius: "6px" }}
                                                                        >
                                                                            <SquarePen size={16} />
                                                                        </Button>
                                                                        {hasDeletePermission && (
                                                                            <Button
                                                                                color="soft-danger"
                                                                                size="sm"
                                                                                onClick={() => handleDelete(t.template_id)}
                                                                                title="Delete Template"
                                                                                style={{ borderRadius: "6px" }}
                                                                            >
                                                                                <Trash2 size={16} />
                                                                            </Button>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ))
                                                    ) : (
                                                        <tr>
                                                            <td colSpan="6" className="text-center">No templates found</td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </Table>
                                        </div>
                                    </TabPane>
                                </TabContent>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </div>
    );
}
